import { FLOWER_IDS, FLOWERS, type FlowerId } from "./flowers";
import { isOut } from "./releases";

/**
 * Player leveling. XP is earned at HARVEST, not at sale — `xpForHarvest` below is the greenhouse's only XP
 * source, and `sellStock` pays coins and nothing else. (BALANCE §2. Selling used to grant XP; it does not,
 * and a sale-priced curve here would silently double-count the same flower.) The level curves (below) make
 * the first session a quick run of level-ups and the climb to L25 tens of hours. Levels gate which seed
 * species the shop offers.
 *
 * Pure functions over a single `xp` total — the App stores `xp` and derives
 * everything (level, progress bar) from it.
 */

/** The hard level CAP for all three tracks (gardening, fishing, bug catching). Gardening's last unlock lands at
 *  MAX_UNLOCK_LEVEL = 50 and the side tracks top out to match. At the cap the XP bar reads full and no further
 *  level-ups fire. */
export const MAX_LEVEL = 50;

/**
 * THE LEVEL CURVES (curve 2, Oct 2026) — one formula, three parameter sets.
 *
 *     cost of level l → l+1  =  round( base + a · l^p · g^max(0, l − CURVE_KNEE) )
 *     total XP to REACH n    =  Σ cost(l) for l = 1 … n−1      (level 1 = 0 xp)
 *
 * WHY THIS SHAPE. The old curves were plain geometric (`80 · 1.20^(l−1)` for gardening), and because XP per
 * hour ALSO climbs ~50× between L1 and L50 (shelves at L3/8/14/22, two better plants a level, grow lights),
 * that made the early game nearly free and the tail a wall: Gardening L25 arrived after 1.7 h of ordinary play
 * and the last seven levels were two thirds of the whole climb. A geometric curve cannot give both "L10 in the
 * first session" and "L25 in tens of hours", so the cost has three parts:
 *   • `base` — the old 1→2 cost, so the first levels still take a few harvests / casts / catches each;
 *   • `a · l^p` — the power-law BODY, which places L10 (~1.5 h) and L25 (~25 h);
 *   • `g^(l−25)` — a geometric TAIL past CURVE_KNEE that sets the length of the late game and nothing else.
 *
 * MEASURED HOURS (realistic = a 75%-efficient player; the level-rebalance report in the git-ignored Kickstarter
 * folder has every level, measured with gardenProgressionSim / fishProgressionSim and a bug-rate model). XP GAINS
 * ARE UNCHANGED — every plant, fish and bug grants exactly what it did on curve 1; only the price of a level
 * moved, so their relative values and the server's per-hour XP limits stay valid.
 *
 *   track         L10      L25     L50      total XP to L50
 *   Gardening     1.5 h    25 h    ~300 h   13,611,220   (curve 1: 3,033,080 — L25 1.7 h, L50 62 h)
 *   Fishing       1.0 h    12 h    ~150 h      675,273   (curve 1:   263,022 — L25 4.0 h, L50 57 h)
 *   Bug catching  1.8 h    12 h    ~150 h      127,893   (curve 1:    52,861 — L25 8.3 h, L50 64 h)
 *
 * The SIDE TRACKS run at about HALF gardening's hours on purpose: gardening hours are wall-clock with the
 * greenhouse growing (it grows in every scene, offline for up to 4 h, and the robot tends it from L28), while
 * fishing and netting each need the player's whole attention. A player who splits a session between them keeps
 * the three levels roughly together, which matters because Kickstarter Mode caps all three at one number and
 * the Triple Crown needs all three at 50.
 *
 * NUMBERS A FUTURE EDITOR MUST NOT CASUALLY CHANGE: `g` alone moves the late game (gardening 0.963 → L50 ≈ 150 h,
 * 1.017 → ≈ 300 h, 1.051 → ≈ 500 h) without touching L1–25; `a`/`p` move the early game. ANY change to a curve
 * re-prices every stored XP total, so it needs a new XP_CURVE_VERSION and a migration step (sim/xpCurve.ts), and
 * the hand copies in server/index.js (pinned by serverMirrors.test.ts) must move in the same commit.
 */
export interface LevelCurve {
  /** flat XP added to every level's cost (the 1→2 cost is ≈ base) */
  base: number;
  /** scale of the power-law body */
  a: number;
  /** exponent of the power-law body */
  p: number;
  /** per-level growth of the tail past CURVE_KNEE */
  g: number;
}
/** The level after which a curve's `g` tail applies. */
export const CURVE_KNEE = 25;
export const GARDEN_CURVE: LevelCurve = { base: 80, a: 7, p: 3, g: 1.017 };
export const FISH_CURVE: LevelCurve = { base: 80, a: 0.72, p: 2.8, g: 1.017 };
export const BUG_CURVE: LevelCurve = { base: 50, a: 1.3, p: 1.95, g: 1.073 };

/** XP cost of going from level `l` to `l + 1` on curve `c`. */
export function levelCost(c: LevelCurve, l: number): number {
  return Math.round(c.base + c.a * Math.pow(l, c.p) * Math.pow(c.g, Math.max(0, l - CURVE_KNEE)));
}
/** Total XP required to have REACHED level `n` on curve `c` (level 1 = 0 xp). */
export function curveTotal(c: LevelCurve, n: number): number {
  let total = 0;
  for (let l = 1; l < n; l++) total += levelCost(c, l);
  return total;
}

/** Total GARDENING XP required to have REACHED level `n`. XP comes from HARVESTING (xpForHarvest below). */
export function xpForLevel(n: number): number { return curveTotal(GARDEN_CURVE, n); }

/** Total FISHING XP required to have reached level `n`. Fishing XP per catch is rarity-scaled (`xpForCatch`), so
 *  chasing the harder fish your gear unlocks is the fastest way up. The late rig tiers (L40/46/48/50) still sit
 *  in the heavy end of this curve (≈63 h / 107 h / 127 h / 150 h of fishing). */
export function fishXpForLevel(n: number): number { return curveTotal(FISH_CURVE, n); }

/**
 * CURVE 1 — the pre-Oct-2026 curves, kept ONLY so a save stored on them can be re-expressed on the current ones
 * (sim/xpCurve.ts `migrateXpCurve`, mirrored in server/index.js). Nothing else may read them.
 */
export function xpForLevelV1(n: number): number {
  let total = 0;
  for (let l = 1; l < n; l++) total += Math.round(80 * Math.pow(1.20, l - 1));
  return total;
}
export function fishXpForLevelV1(n: number): number {
  let total = 0;
  for (let l = 1; l < n; l++) total += Math.round(80 * Math.pow(1.132, l - 1));
  return total;
}
export function bugXpForLevelV1(n: number): number {
  let total = 0;
  for (let l = 1; l < n; l++) total += Math.round(50 * Math.pow(1.10, l - 1));
  return total;
}
/** Fishing LevelInfo from a fishing-xp total, using the fishing curve. */
export function fishLevelInfo(xp: number): LevelInfo {
  let level = 1;
  while (level < MAX_LEVEL && xp >= fishXpForLevel(level + 1)) level++;
  if (level >= MAX_LEVEL) { const span = Math.max(1, fishXpForLevel(MAX_LEVEL) - fishXpForLevel(MAX_LEVEL - 1)); return { level: MAX_LEVEL, intoLevel: span, span, progress: 1 }; }
  const base = fishXpForLevel(level), next = fishXpForLevel(level + 1), span = Math.max(1, next - base);
  return { level, intoLevel: xp - base, span, progress: Math.min(1, (xp - base) / span) };
}

/**
 * XP granted for CATCHING one fish. Super-linear in the fish's coin VALUE (like harvest XP) so a rarer/harder
 * fish grants disproportionately more — and because the rarer fish only become catchable as you climb gear/
 * levels, chasing them is the fastest path. Tuned with `fishXpForLevel` (see the progression sim).
 */
export const CATCH_XP_K = 1.1;
export const CATCH_XP_EXP = 1.15;
export function xpForCatch(fishValue: number): number {
  return Math.max(3, Math.round(CATCH_XP_K * Math.pow(Math.max(0, fishValue), CATCH_XP_EXP)));
}

/**
 * Total NATURALIST (bug-catching) XP required to have reached level `n`. A catch is worth `max(2, round(value ·
 * 0.4))` (collectInsect in useGarden.ts) — ~7–8 XP on average, an order of magnitude under a fish — and the
 * garden offers a bug every ~22 s (DEFAULT_SPAWN_CONFIG), so a dedicated netter lands ~80–160 catches an hour
 * depending on level. That is why this is the smallest of the three curves in XP while matching fishing in hours.
 * The TALENT ladder unlocks a tier every 5 levels (bugTalents.ts): tier 1 ≈ 35 min of netting, tier 5 ≈ 12 h,
 * tier 10 ≈ 150 h.
 */
export function bugXpForLevel(n: number): number { return curveTotal(BUG_CURVE, n); }
/** Naturalist LevelInfo from a bug-xp total, using the Naturalist curve. */
export function bugLevelInfo(xp: number): LevelInfo {
  let level = 1;
  while (level < MAX_LEVEL && xp >= bugXpForLevel(level + 1)) level++;
  if (level >= MAX_LEVEL) { const span = Math.max(1, bugXpForLevel(MAX_LEVEL) - bugXpForLevel(MAX_LEVEL - 1)); return { level: MAX_LEVEL, intoLevel: span, span, progress: 1 }; }
  const base = bugXpForLevel(level), next = bugXpForLevel(level + 1), span = Math.max(1, next - base);
  return { level, intoLevel: xp - base, span, progress: Math.min(1, (xp - base) / span) };
}

export interface LevelInfo {
  level: number;
  /** xp into the current level. */
  intoLevel: number;
  /** xp needed to span the current level (intoLevel / span = bar fill). */
  span: number;
  /** 0..1 progress to the next level. */
  progress: number;
}

export function levelInfo(xp: number): LevelInfo {
  let level = 1;
  while (level < MAX_LEVEL && xp >= xpForLevel(level + 1)) level++;
  // At the cap the bar reads FULL and there is no "next level" — clamp progress to 1.
  if (level >= MAX_LEVEL) {
    const span = Math.max(1, xpForLevel(MAX_LEVEL) - xpForLevel(MAX_LEVEL - 1));
    return { level: MAX_LEVEL, intoLevel: span, span, progress: 1 };
  }
  const base = xpForLevel(level);
  const next = xpForLevel(level + 1);
  const span = Math.max(1, next - base);
  const intoLevel = xp - base;
  return { level, intoLevel, span, progress: Math.min(1, intoLevel / span) };
}

/**
 * XP granted for HARVESTING one flower of the given `sellValue`. This is the greenhouse's core XP source.
 *
 * SUPER-LINEAR in sellValue (`K · sellValue^EXP`, EXP > 1): higher-value plants grant disproportionately
 * more XP. Because the flat economy makes grow-time ∝ sellValue, an EXP of 1 would give every plant the SAME
 * XP/hour; EXP = 1.25 makes a top-tier plant earn ~65% more XP PER HOUR than a starter, so planting the
 * highest-value unlocked species is genuinely the fastest way to level.
 *
 * XP GAINS ARE NOT THE PACING KNOB. The time to each level is set by the curves above (GARDEN_CURVE); this
 * constant sets the RELATIVE worth of plants. Raising K would shorten every level at once and invalidate the
 * server's per-hour XP limit, which is sized from these gains (server/index.js CURRENCY_LIMITS.xp).
 */
export const HARVEST_XP_K = 0.24;
export const HARVEST_XP_EXP = 1.25;
export function xpForHarvest(sellValue: number): number {
  return Math.max(1, Math.round(HARVEST_XP_K * Math.pow(Math.max(0, sellValue), HARVEST_XP_EXP)));
}

/**
 * PLANT PROGRESSION. The player starts with the first TWO plants and unlocks TWO MORE each level, in a
 * fixed authored order that interleaves plant FORMS (bush / rosette / grass / trailing / cactus / fern
 * / shrub) and rises gently in value — so every level-up feels varied, not "all bushes for 16 levels".
 *
 * The 84 real plants fill levels 1..42 (2/level). The 16 EXOTIC FANTASY plants live in a separate tier
 * that is LOCKED until the player reaches EXOTIC_LICENSE_LEVEL and buys an Exotic Plant License; once
 * licensed, fantasy plants also unlock two per level. Crossing/growing isn't gated — only the shop.
 */

/** The authored unlock order of the 84 non-fantasy plants (index 0,1 → level 1; 2,3 → level 2; …). */
const PROGRESSION_ORDER: FlowerId[] = [
  "daisy", "henChicks", "mondoGrass", "creepingJenny", "pincushionCactus", "asparagusFern",
  "rosemary", "marigold", "sempervivum", "blueFescue", "nasturtium", "jade",
  "buttonFern", "boxwood", "pansy", "haworthia", "sedge", "trailingLobelia",
  "snakePlant", "bostonFern", "lavenderShrub", "crocus", "aloe", "horsetail",
  "englishIvy", "stringPearls", "birdsNestFern", "pepperPlant", "zinnia", "echeveria",
  "fountainGrass", "wateringIvy", "barrelCactus", "maidenhairFern", "winterberry", "petunia",
  "agave", "purpleMillet", "pothos", "zebraCactus", "staghornFern", "holly",
  "cosmos", "papyrus", "sweetPea", "pricklyPear", "azalea", "blackEyedSusan",
  "pampasGrass", "philodendron", "goldenCactus", "miniRoseBush", "poppy", "ivyGeranium",
  "hydrangeaBush", "aster", "morningGlory", "rhododendron", "bluebell", "stringHearts",
  "gardenia", "carnation", "burrosTail", "camellia", "geranium", "gerbera",
  "daffodil", "lavender", "chrysanthemum", "snapdragon", "hyacinth", "anemone",
  "tulip", "freesia", "allium", "ranunculus", "iris", "gladiolus",
  "foxglove", "delphinium", "sunflower", "hibiscus", "peony", "rose",
];

/** The 16 exotic FANTASY plants, in their own unlock order (behind the license). */
const FANTASY_ORDER: FlowerId[] = [
  "lumenCactus", "pearlFern", "cometvine", "crystalbloom", "amethystThorn", "prismRose",
  "emberbloom", "phoenixLily", "magmaSucculent", "frostfern", "glacierlily", "cloudPuff",
  "willowisp", "honeybell", "lanternMoss", "glassvine",
];

/** PREMIUM plants: NEVER unlockable by leveling (or the exotic license) — only granted by a real-money
 *  shop purchase (an entitlement, see payments.ts myEntitlements → App.tsx ownedPlants). Kept OUT of the
 *  progression/fantasy orders; `isUnlocked` short-circuits to false so ONLY the entitlement can grant them. */
export const PREMIUM_PLANT_IDS: readonly FlowerId[] = ["voidOrchid", "auroraFern", "lilyOfTheDead", "waterDandelion", "vampiricPitcher", "nightglowMushroom", "bloodshadowRose", "lightbloom", "abyssBlossom"];
const PREMIUM_PLANTS = new Set<FlowerId>(PREMIUM_PLANT_IDS);
/** Is this a premium (shop-only) plant that leveling can never unlock? */
export function isPremium(id: FlowerId): boolean { return PREMIUM_PLANTS.has(id); }

/** Every harvestable species in CATALOGUE order — the unlock ladder, then the fantasy set, then premium —
 *  i.e. the 109 the Complete Herbarium counts. The Field Guide's Plants tab lists ALL of it, unfiltered (a
 *  premium plant the player does not own shows a "Premium Shop" tag), so its x/109 matches the achievement. */
export const HERBARIUM_ORDER: readonly FlowerId[] = [...PROGRESSION_ORDER, ...FANTASY_ORDER, ...PREMIUM_PLANTS];

const PLANTS_PER_LEVEL = 2;
/** Level at which the Exotic Plant License becomes available (just after the 84 real plants). */
export const EXOTIC_LICENSE_LEVEL = Math.ceil(PROGRESSION_ORDER.length / PLANTS_PER_LEVEL) + 1; // 43
/** One-time coin cost of the Exotic Plant License (a large late-game sink). */
export const EXOTIC_LICENSE_PRICE = 5000;

/** Level at which HARVEST ALL appears on the greenhouse Tools menu (below it the item shows locked, naming
 *  this level). 11 sits mid-ladder in a level that opened nothing but seeds — 9 is the growth timer, 12 the
 *  compost machine, 14 the fourth shelf — by which point the player is clicking through 36+ pots a round. */
export const HARVEST_ALL_UNLOCK_LEVEL = 11;

const progressionIndex = new Map(PROGRESSION_ORDER.map((id, i) => [id, i]));
const fantasyIndex = new Map(FANTASY_ORDER.map((id, i) => [id, i]));

/** The highest level at which anything new unlocks — i.e. once here, every plant is available (given the
 *  license). Used by the dev "Max level" cheat. */
export const MAX_UNLOCK_LEVEL =
  EXOTIC_LICENSE_LEVEL + Math.ceil(FANTASY_ORDER.length / PLANTS_PER_LEVEL) - 1;

/** Is this species one of the exotic fantasy plants (license-gated)? */
export function isFantasy(id: FlowerId): boolean {
  return fantasyIndex.has(id);
}

/**
 * The LEVEL at which a species becomes purchasable. Non-fantasy: its ordered pair's level. Fantasy:
 * its ordered pair's level OFFSET past the license level (so buying the license at L43 opens the first
 * fantasy pair, the next pair at L44, …). A fantasy plant ALSO requires the license (see isUnlocked).
 */
export function unlockLevel(id: FlowerId): number {
  const pi = progressionIndex.get(id);
  if (pi !== undefined) return Math.floor(pi / PLANTS_PER_LEVEL) + 1;
  const fi = fantasyIndex.get(id);
  if (fi !== undefined) return EXOTIC_LICENSE_LEVEL + Math.floor(fi / PLANTS_PER_LEVEL);
  return 1;   // off both orders: the 9 premium plants (isUnlocked refuses them before reading this) or an unknown id
}

/** Purchasable now? Fantasy plants additionally require the Exotic Plant License. PREMIUM plants are NEVER
 *  unlocked here — only a shop entitlement grants them (App.tsx ORs in ownedPlants.has(id)). */
export function isUnlocked(id: FlowerId, level: number, hasExoticLicense = false): boolean {
  if (isOut(FLOWERS[id])) return false;   // (a content release that is not out yet — sim/releases.ts)
  if (isPremium(id)) return false;
  if (level < unlockLevel(id)) return false;
  if (isFantasy(id) && !hasExoticLicense) return false;
  return true;
}
/** Species newly unlocked exactly at `level` (for a level-up toast). Fantasy plants are only surfaced
 *  here once the player holds the license; premium plants never appear (they're purchase-only). */
export function unlockedAt(level: number, hasExoticLicense = false): FlowerId[] {
  return FLOWER_IDS.filter((id) => !isPremium(id) && !isOut(FLOWERS[id]) && unlockLevel(id) === level && (!isFantasy(id) || hasExoticLicense));
}
